import {
  getFulfillmentStatusLabel,
  getFulfillmentStatusSeverity,
} from '@app/shared/order-status/order-status.util';
import { InternalOrderConfig } from '../../order/internal/internal.config';

/** O2O internal list — orderStatus (order workflow) display keys. */
const O2O_ORDER_STATUS_LABEL_BY_CODE: Record<string, string> = {
  '0': 'o2o.internal.order_status_labels.not_started',
  '30000': 'o2o.internal.order_status_labels.init_normal',
  '30101': 'o2o.internal.order_status_labels.preemption_wait',
  '30111': 'o2o.internal.order_status_labels.preemption_write_wait',
  '30112': 'o2o.internal.order_status_labels.preemption_write_success',
  '30113': 'o2o.internal.order_status_labels.preemption_write_fail',
  '30211': 'o2o.internal.order_status_labels.preemption_audit_one_wait',
  '30212': 'o2o.internal.order_status_labels.preemption_audit_one_success',
  '30213': 'o2o.internal.order_status_labels.preemption_audit_one_fail',
  '30301': 'o2o.internal.order_status_labels.sourcing_wait',
  '30302': 'o2o.internal.order_status_labels.sourcing_success',
  '30303': 'o2o.internal.order_status_labels.sourcing_fail',
  '30304': 'o2o.internal.order_status_labels.sourcing_part_success',
  '30305': 'o2o.internal.order_status_labels.sourcing_cancel',
  '30510': 'o2o.internal.order_status_labels.send_init',
  '30511': 'o2o.internal.order_status_labels.send_wait',
  '30512': 'o2o.internal.order_status_labels.send_success',
  '30513': 'o2o.internal.order_status_labels.send_fail',
  '30514': 'o2o.internal.order_status_labels.send_part_success',
  '30516': 'o2o.internal.order_status_labels.send_picked',
  '30517': 'o2o.internal.order_status_labels.send_ready_for_pickup',
  '30521': 'o2o.internal.order_status_labels.receive_wait',
  '30522': 'o2o.internal.order_status_labels.receive_success',
  '30523': 'o2o.internal.order_status_labels.part_receive_success',
  '30531': 'o2o.internal.order_status_labels.send_write_wait',
  '30532': 'o2o.internal.order_status_labels.send_write_success',
  '30533': 'o2o.internal.order_status_labels.send_write_fail',
  '31000': 'o2o.internal.order_status_labels.complete',
  '31010': 'o2o.internal.order_status_labels.cancel',
  '31120': 'o2o.internal.order_status_labels.manual_intervention',
};

const O2O_ORDER_STATUS_SUCCESS_CODES = new Set([
  '30112',
  '30212',
  '30302',
  '30512',
  '30522',
  '30532',
  '31000',
]);

const O2O_ORDER_STATUS_FAIL_CODES = new Set([
  '30113',
  '30213',
  '30303',
  '30513',
  '30533',
]);

const O2O_ORDER_STATUS_WARN_CODES = new Set([
  '30101',
  '30111',
  '30211',
  '30301',
  '30304',
  '30511',
  '30514',
  '30516',
  '30517',
  '30521',
  '30523',
  '30531',
  '31120',
]);

/** O2O internal list only — order-level delivery (SendStatus) display keys. */
const O2O_DELIVERY_LABEL_BY_CODE: Record<string, string> = {
  '30511': 'o2o.internal.delivery_status_labels.pending_outbound',
  '30512': 'o2o.internal.delivery_status_labels.outbound_success',
  '30515': 'o2o.internal.delivery_status_labels.returning',
  '30516': 'o2o.internal.order_status_labels.send_picked',
  '30517': 'o2o.internal.order_status_labels.send_ready_for_pickup',
};

/** O2O internal list only — order-level receiving display keys. */
const O2O_RECEIVING_LABEL_BY_CODE: Record<string, string> = {
  '30521': 'o2o.internal.receiving_status_labels.pending',
  '30525': 'o2o.internal.receiving_status_labels.picked_up',
  '30526': 'o2o.internal.receiving_status_labels.transit_between_hubs',
  '30527': 'o2o.internal.receiving_status_labels.out_for_delivery',
  '30522': 'o2o.internal.receiving_status_labels.receive_success',
  '30524': 'o2o.internal.receiving_status_labels.receive_failed',
  '30528': 'o2o.internal.order_status_labels.awaiting_retry',
  '30529': 'o2o.internal.order_status_labels.return_stock_success',
};

function o2oOrderStatusSeverity(code: string): string {
  if (O2O_ORDER_STATUS_FAIL_CODES.has(code)) {
    return 'danger';
  }
  if (code === '31010' || code === '30305') {
    return 'secondary';
  }
  if (O2O_ORDER_STATUS_SUCCESS_CODES.has(code)) {
    return 'success';
  }
  if (O2O_ORDER_STATUS_WARN_CODES.has(code)) {
    return 'warn';
  }
  if (code === '0' || code === '30000' || code === '30510') {
    return 'info';
  }
  return 'secondary';
}

export function o2oInternalGetStatusDisplayText(
  columnProp: string,
  value: unknown,
): string {
  const code = String(value ?? '');
  if (columnProp === 'orderStatus') {
    return (
      O2O_ORDER_STATUS_LABEL_BY_CODE[code] ??
      InternalOrderConfig.getOrderStatusLabel(code)
    );
  }
  if (columnProp === 'fulfillmentStatus') {
    return (
      O2O_ORDER_STATUS_LABEL_BY_CODE[code] ?? getFulfillmentStatusLabel(code)
    );
  }
  if (columnProp === 'deliveryStatus') {
    return (
      O2O_DELIVERY_LABEL_BY_CODE[code] ??
      InternalOrderConfig.getDeliveryStatusLabel(code)
    );
  }
  if (columnProp === 'receivingStatus') {
    return (
      O2O_RECEIVING_LABEL_BY_CODE[code] ??
      InternalOrderConfig.getReceivingStatusLabel(code)
    );
  }
  return InternalOrderConfig.getStatusDisplayText(columnProp, value);
}

export function o2oInternalGetStatusSeverity(
  columnProp: string,
  value: unknown,
): string {
  if (columnProp === 'orderStatus') {
    const v = String(value ?? '');
    if (Object.prototype.hasOwnProperty.call(O2O_ORDER_STATUS_LABEL_BY_CODE, v)) {
      return o2oOrderStatusSeverity(v);
    }
    return InternalOrderConfig.getStatusSeverity('orderStatus', v);
  }
  if (columnProp === 'fulfillmentStatus') {
    const v = String(value ?? '');
    if (Object.prototype.hasOwnProperty.call(O2O_ORDER_STATUS_LABEL_BY_CODE, v)) {
      return o2oOrderStatusSeverity(v);
    }
    return getFulfillmentStatusSeverity(v);
  }
  if (columnProp === 'deliveryStatus') {
    const v = String(value ?? '');
    if (Object.prototype.hasOwnProperty.call(O2O_DELIVERY_LABEL_BY_CODE, v)) {
      if (v === '30511' || v === '30515') return 'warn';
      if (v === '30512' || v === '30516') return 'success';
      return 'secondary';
    }
    return InternalOrderConfig.getStatusSeverity('deliveryStatus', v);
  }
  if (columnProp === 'receivingStatus') {
    const v = String(value ?? '');
    if (Object.prototype.hasOwnProperty.call(O2O_RECEIVING_LABEL_BY_CODE, v)) {
      if (v === '30524' || v === '30528') return 'danger';
      if (v === '30529') return 'success';
      if (v === '30522') return 'success';
      return 'warn';
    }
    return InternalOrderConfig.getStatusSeverity('receivingStatus', v);
  }
  return InternalOrderConfig.getStatusSeverity(columnProp, value);
}
